import {Html5Qrcode} from 'html5-qrcode';
import Async from '../Async';

class CodeScanner {
    /**
     * @method constructor
     */
    constructor() {
        // Declare and initialize
        this.modal        = $('#ticket-checkin-modal');
        this.readerId     = 'ticket-checkin-reader';
        this.status       = $('[data-checkin-status]');
        this.scanner      = null;
        this.isStarting   = false;
        this.isScanning   = false;
        this.isSubmitting = false;
        this.async        = new Async();

        // Run initialization
        this.init();
    }

    /**
     * Initialize scanner modal events.
     *
     * @method init
     * @since 1.0.0
     * @returns void
     */
    init() {
        // Start when the modal is fully visible
        this.modal.on('shown.bs.modal', () => {
            this.start();
        });

        // Stop when the modal is fully hidden
        this.modal.on('hidden.bs.modal', () => {
            this.stop();
        });
    }

    /**
     * Update the modal status text.
     *
     * @method setStatus
     * @since 1.0.0
     * @param string message The status message
     * @param string type The Bootstrap alert type
     * @returns void
     */
    setStatus(message, type = 'info') {
        // Update classes and text
        this.status
            .removeClass('alert-info alert-success alert-danger alert-warning')
            .addClass('alert-' + type)
            .text(message);
    }

    /**
     * Start the QR scanner.
     *
     * @method start
     * @since 1.0.0
     * @returns void
     */
    async start() {
        // Prevent duplicate starts
        if (this.isStarting || this.isScanning)
            return;

        // Update state
        this.isStarting = true;
        this.setStatus('Starting camera...', 'info');

        // Declare and initialize
        const auxData = this.modal.data('code-scanner-aux-data');

        try {
            // Build the scanner once
            if (!this.scanner)
                this.scanner = new Html5Qrcode(this.readerId);

            // Start camera scanning
            await this.scanner.start(
                {facingMode: 'environment'},
                {
                    fps:   10,
                    qrbox: {width: 260, height: 260}
                },
                (decodedText) => {
                    this.onScanSuccess(decodedText, auxData);
                },
                () => {
                    // Ignore frame-by-frame misses
                }
            );

            // Update state
            this.isScanning = true;
            this.setStatus('Point the camera at the QR code.', 'info');
        } catch (error) {
            // Surface the issue
            this.setStatus('Unable to access the camera.', 'danger');
            console.error(error);
        } finally {
            // Reset state
            this.isStarting = false;
        }
    }

    /**
     * Stop the QR scanner.
     *
     * @method stop
     * @since 1.0.0
     * @returns void
     */
    async stop() {
        // If there is no scanner, bail
        if (!this.scanner)
            return;

        // If scanning never fully started, clear the container and bail
        if (!this.isScanning) {
            $('#' + this.readerId).empty();
            return;
        }

        try {
            // Stop camera scanning
            await this.scanner.stop();
        } catch (error) {
            console.error(error);
        }

        // Clear state
        this.isScanning   = false;
        this.isSubmitting = false;

        // Empty the reader markup so reopening is clean
        $('#' + this.readerId).empty();
    }

    /**
     * Handle a successful scan.
     *
     * @method onScanSuccess
     * @since 1.0.0
     * @param string decodedText is the scanned QR text
     * @param mixed auxData is any extra data to send with the request
     * @returns void
     */
    async onScanSuccess(decodedText, auxData) {
        // Prevent duplicate submissions
        if (this.isSubmitting)
            return;

        // Lock submissions
        this.isSubmitting = true;
        this.setStatus('Checking in ticket...', 'warning');

        // Pause scanning while we validate
        if (this.scanner && this.isScanning)
            this.scanner.pause(true);

        // Post the scanned value
        this.async.post('check-in', {
            params:  {
                code:       decodedText,
                event_id:   auxData.event_id,
                recur_date: auxData.recur_date
            },
            log:     true,
            success: {
                callback: (response) => {

                    // If the check-in failed softly, handle it here
                    if (!response || !response.success) {
                        this.setStatus(
                            response && response.message
                                ? response.message
                                : 'Unable to check in ticket.',
                            'danger'
                        );

                        if (this.scanner && this.isScanning)
                            this.scanner.resume();

                        this.isSubmitting = false;
                        return;
                    }

                    // Show success
                    this.setStatus(response.message || 'Ticket checked in.', 'success');

                    // Close the modal after a beat
                    setTimeout(() => {
                        this.modal.modal('hide');
                    }, 900);
                }
            },
            fail:    {
                callback: (response) => {
                    this.setStatus(
                        response && response.message
                            ? response.message
                            : 'An error occurred.',
                        'danger'
                    );

                    // Resume scanning after failure
                    if (this.scanner && this.isScanning)
                        this.scanner.resume();

                    this.isSubmitting = false;
                }
            }
        });
    }
}

export default CodeScanner;